'use client';

import React, { useState, useEffect, useRef } from 'react';

interface NavItem {
  label: string;
  href: string;
}

const navItems: NavItem[] = [
  { label: 'Home', href: '#home' },
  { label: 'About', href: '#about' },
  { label: 'Services', href: '#services' },
  { label: 'Why Choose Us', href: '#why-us' },
  { label: 'Contact', href: '#contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href: string) => {
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-md py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center">
            <span className="text-white font-black text-sm">P7</span>
          </div>
          <div>
            <span className={`font-bold text-lg leading-tight block transition-colors duration-300 ${
              scrolled ? 'text-navy-dark' : 'text-white'
            }`}>
              Pluto7
            </span>
            <span className={`text-xs leading-tight block transition-colors duration-300 ${
              scrolled ? 'text-gray-500' : 'text-blue-200'
            }`}>
              Consulting Inc.
            </span>
          </div>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => (
            <button
              key={item.label}
              onClick={() => scrollToSection(item.href)}
              className={`text-sm font-medium transition-colors duration-200 hover:text-primary ${
                scrolled ? 'text-gray-700' : 'text-gray-200'
              }`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* CTA Button */}
        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={() => scrollToSection('#contact')}
            className="px-5 py-2.5 text-sm font-semibold text-white rounded-btn bg-btn-primary shadow-btn hover:opacity-90 transition-all duration-200"
          >
            Request a Consultation
          </button>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="lg:hidden p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <div className={`w-6 h-0.5 mb-1.5 transition-all duration-300 ${
            scrolled ? 'bg-gray-800' : 'bg-white'
          } ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`} />
          <div className={`w-6 h-0.5 mb-1.5 transition-all duration-300 ${
            scrolled ? 'bg-gray-800' : 'bg-white'
          } ${mobileOpen ? 'opacity-0' : ''}`} />
          <div className={`w-6 h-0.5 transition-all duration-300 ${
            scrolled ? 'bg-gray-800' : 'bg-white'
          } ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-4">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.href)}
                className="text-left text-gray-700 font-medium py-2 hover:text-primary transition-colors"
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => scrollToSection('#contact')}
              className="mt-2 px-5 py-3 text-sm font-semibold text-white rounded-btn bg-btn-primary shadow-btn"
            >
              Request a Consultation
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
